import { Trans } from "@lingui/react/macro";
import { type ReactNode } from "react";

import { Tooltip } from "../../../components/ui-kit/base/tooltip";
import { Button } from "../../../components/ui-kit/button";
import { ErrorMessage } from "../../../components/ui-kit/error-message";
import { usePasskeySupport } from "./passkey";

/** Lets the user pick which second factor to set up. */
export function MethodChoice({
  reason,
  errorMessage,
  onPasskey,
  onAuthenticatorApp,
  onCancel,
  cancelLabel,
}: {
  reason?: ReactNode;
  /** Why the last attempt didn't work, if it didn't. */
  errorMessage: string;
  onPasskey: () => void | Promise<unknown>;
  onAuthenticatorApp: () => void | Promise<unknown>;
  onCancel: () => void;
  cancelLabel: ReactNode;
}) {
  const passkeySupport = usePasskeySupport();

  return (
    <div className="flex flex-col items-center" data-testid="mfa-method-choice">
      <h2 className="text-xl font-semibold mb-4">
        <Trans>Set up two-factor authentication</Trans>
      </h2>

      {reason && <p className="text-sm text-center max-w-96 mb-2">{reason}</p>}

      <p className="text-sm text-center max-w-96">
        <Trans>
          A passkey lets you confirm it's you with your fingerprint, face or
          device PIN. An authenticator app gives you a code to type in instead.
        </Trans>
      </p>

      <div className="flex flex-col gap-2 mt-6">
        <Tooltip content={passkeySupport.unsupportedReason}>
          <Button
            variant="primary"
            disabled={!passkeySupport.supported}
            onClick={onPasskey}
          >
            <Trans>Use a passkey</Trans>
          </Button>
        </Tooltip>
        <Button onClick={onAuthenticatorApp}>
          <Trans>Use an authenticator app</Trans>
        </Button>
      </div>

      <ErrorMessage message={errorMessage} />

      <a
        onClick={onCancel}
        className="text-xs text-brand-primary hover:underline cursor-pointer mt-8"
      >
        {cancelLabel}
      </a>
    </div>
  );
}
